// The signed-in producer's library ("Mine"): clips, MIDI ideas and the
// folders they sit in, for the room's Material shelf and the upload page.
//
// Only the owner ever lists it (the API is owner-only). The owner hears a
// clip — its preview, and a LOCAL room's placement before any save — through
// the account worker's object proxy, which opens a pool blob to the owner of
// a library clip holding it (worker/account.ts keyAccess). Not the signed R2
// URL the API also returns: the bucket has no CORS, so a page can't fetch it.
// Connected rooms take a jam ref instead (useMineShelf), so every participant
// can fetch a placed clip.
//
// One GET returns the whole library; every change invalidates it. The tree
// is small, and a folder rename that waited on a refetch would still beat a
// second request to the shelf.

import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  BulkTagRequest,
  ClipUsesResponse,
  CreateFolderRequest,
  CreateSmartFolderRequest,
  DeleteFolderResponse,
  MoveLibraryRequest,
  MyClip,
  MyFolder,
  MyLibraryResponse,
  MyMidi,
  MySmartFolder,
  NewMidiRequest,
  MyTag,
  RenameTagRequest,
  ReviewTagsRequest,
  TouchRecentRequest,
  UpdateClipRequest,
  UpdateFolderRequest,
  UpdateMidiRequest,
  UpdateSmartFolderRequest,
} from "@shared/api-types.js";
import { objectUrl } from "@web-shared/lib/api";
import { applyReview, applyTagDelta } from "@web-shared/lib/material-item";
import { json, webApi } from "@web-shared/lib/web-api";
import { useSession } from "../session";

export const MY_CLIPS_KEY = ["web", "my-clips"] as const;

const EMPTY: MyLibraryResponse = { folders: [], clips: [], midi: [], smartFolders: [], usedBytes: 0, quotaBytes: 0, recent: [] };

/** A clip or MIDI row with a patch applied the way the server will — tags
 * as a delta, everything else as a field. */
function patched<T extends { tags: MyClip["tags"] }>(row: T, body: Omit<Partial<T>, "tags"> & { tags?: UpdateClipRequest["tags"] }): T {
  const { tags, ...rest } = body;
  return { ...row, ...rest, ...(tags ? { tags: applyTagDelta(row.tags, tags) } : {}) } as T;
}

export function useMyClips() {
  const session = useSession();
  const queryClient = useQueryClient();
  const userId = session?.user.id ?? null;
  const key = useMemo(() => [...MY_CLIPS_KEY, userId ?? ""] as const, [userId]);
  const query = useQuery({
    queryKey: key,
    enabled: userId !== null,
    queryFn: () => webApi<MyLibraryResponse>("/library"),
    // The signed URLs live two hours; refetch well inside that.
    staleTime: 30 * 60 * 1000,
  });
  const invalidate = useCallback(() => queryClient.invalidateQueries({ queryKey: MY_CLIPS_KEY }), [queryClient]);
  const patchCache = useCallback(
    (fn: (old: MyLibraryResponse) => MyLibraryResponse) => {
      queryClient.setQueryData<MyLibraryResponse>(key, (old) => (old ? fn(old) : old));
    },
    [queryClient, key],
  );

  const remove = useMutation({
    mutationFn: (id: string) => webApi<{ ok: true; usedBytes: number }>(`/library/clips/${encodeURIComponent(id)}`, json("DELETE")),
    onSuccess: (_r, id) => {
      patchCache((old) => ({ ...old, clips: old.clips.filter((c) => c.id !== id) }));
      void invalidate();
    },
  });
  const removeMidi = useMutation({
    mutationFn: (id: string) => webApi<{ ok: true }>(`/library/midi/${encodeURIComponent(id)}`, json("DELETE")),
    onSuccess: (_r, id) => {
      patchCache((old) => ({ ...old, midi: old.midi.filter((m) => m.id !== id) }));
      void invalidate();
    },
  });

  // Folders.
  const createFolder = useMutation({
    mutationFn: (body: CreateFolderRequest) => webApi<MyFolder>("/library/folders", json("POST", body)),
    onSuccess: (folder) => {
      patchCache((old) => ({ ...old, folders: [...old.folders, folder] }));
      void invalidate();
    },
  });
  const updateFolder = useMutation({
    mutationFn: ({ id, ...body }: UpdateFolderRequest & { id: string }) => webApi<MyFolder>(`/library/folders/${encodeURIComponent(id)}`, json("PATCH", body)),
    onSuccess: (folder) => {
      patchCache((old) => ({ ...old, folders: old.folders.map((f) => (f.id === folder.id ? folder : f)) }));
      void invalidate();
    },
  });
  const deleteFolder = useMutation({
    mutationFn: (id: string) => webApi<DeleteFolderResponse>(`/library/folders/${encodeURIComponent(id)}`, json("DELETE")),
    onSuccess: (res, id) => {
      // What the server did: contents step up to the parent.
      patchCache((old) => ({
        ...old,
        folders: old.folders.filter((f) => f.id !== id).map((f) => (f.parentId === id ? { ...f, parentId: res.movedTo } : f)),
        clips: old.clips.map((c) => (c.folderId === id ? { ...c, folderId: res.movedTo } : c)),
        midi: old.midi.map((m) => (m.folderId === id ? { ...m, folderId: res.movedTo } : m)),
      }));
      void invalidate();
    },
  });
  const move = useMutation({
    mutationFn: (body: MoveLibraryRequest) => webApi<{ ok: true; moved: number }>("/library/move", json("POST", body)),
    onMutate: (body) => {
      // Optimistic: the row is already where the finger put it.
      patchCache((old) => ({
        ...old,
        clips: old.clips.map((c) => (body.clipIds.includes(c.id) ? { ...c, folderId: body.folderId } : c)),
        midi: old.midi.map((m) => (body.midiIds.includes(m.id) ? { ...m, folderId: body.folderId } : m)),
      }));
    },
    onSettled: () => void invalidate(),
  });

  // Stars and edits.
  const updateClip = useMutation({
    mutationFn: ({ id, ...body }: UpdateClipRequest & { id: string }) => webApi<MyClip>(`/library/clips/${encodeURIComponent(id)}`, json("PATCH", body)),
    onMutate: ({ id, ...body }) => {
      patchCache((old) => ({ ...old, clips: old.clips.map((c) => (c.id === id ? patched(c, body) : c)) }));
    },
    onSettled: () => void invalidate(),
  });
  const updateMidi = useMutation({
    mutationFn: ({ id, ...body }: UpdateMidiRequest & { id: string }) => webApi<MyMidi>(`/library/midi/${encodeURIComponent(id)}`, json("PATCH", body)),
    onMutate: ({ id, ...body }) => {
      patchCache((old) => ({ ...old, midi: old.midi.map((m) => (m.id === id ? patched(m, body) : m)) }));
    },
    onSettled: () => void invalidate(),
  });
  /** One delta on many rows: a multi-select tagged at once. */
  const tagMany = useMutation({
    mutationFn: (body: BulkTagRequest) => webApi<{ ok: true; changed: number }>("/library/tags", json("POST", body)),
    onMutate: (body) => {
      const delta = { set: body.set, unset: body.unset, suggest: body.suggest };
      patchCache((old) => ({
        ...old,
        clips: old.clips.map((c) => (body.clipIds.includes(c.id) ? { ...c, tags: applyTagDelta(c.tags, delta) } : c)),
        midi: old.midi.map((m) => (body.midiIds.includes(m.id) ? { ...m, tags: applyTagDelta(m.tags, delta) } : m)),
      }));
    },
    onSettled: () => void invalidate(),
  });

  /** A tag renamed (or merged) everywhere it is. */
  const renameTag = useMutation({
    mutationFn: (body: RenameTagRequest) => webApi<{ ok: true; changed: number }>("/library/tags/rename", json("POST", body)),
    onMutate: ({ from, to }) => {
      const moved = (tags: MyTag[]): MyTag[] => {
        const has = tags.some((t) => t.tag === to);
        return tags.flatMap((t) => (t.tag !== from ? [t] : has ? [] : [{ ...t, tag: to }]));
      };
      patchCache((old) => ({
        ...old,
        clips: old.clips.map((c) => ({ ...c, tags: moved(c.tags) })),
        midi: old.midi.map((m) => ({ ...m, tags: moved(m.tags) })),
        smartFolders: old.smartFolders.map((sf) => ({ ...sf, chips: [...new Set(sf.chips.map((ch) => (ch === `tag:${from}` ? `tag:${to}` : ch)))] })),
      }));
    },
    onSettled: () => void invalidate(),
  });

  /** The suggestions of many rows, kept or dismissed at once. */
  const review = useMutation({
    mutationFn: (body: ReviewTagsRequest) => webApi<{ ok: true; changed: number }>("/library/tags/review", json("POST", body)),
    onMutate: (body) => {
      patchCache((old) => ({
        ...old,
        clips: old.clips.map((c) => (body.clipIds.includes(c.id) ? { ...c, tags: applyReview(c.tags, body) } : c)),
        midi: old.midi.map((m) => (body.midiIds.includes(m.id) ? { ...m, tags: applyReview(m.tags, body) } : m)),
      }));
    },
    onSettled: () => void invalidate(),
  });

  // Smart folders: saved searches.
  const createSmartFolder = useMutation({
    mutationFn: (body: CreateSmartFolderRequest) => webApi<MySmartFolder>("/library/smart-folders", json("POST", body)),
    onSuccess: (sf) => {
      patchCache((old) => ({ ...old, smartFolders: [...old.smartFolders, sf] }));
      void invalidate();
    },
  });
  const updateSmartFolder = useMutation({
    mutationFn: ({ id, ...body }: UpdateSmartFolderRequest & { id: string }) => webApi<MySmartFolder>(`/library/smart-folders/${encodeURIComponent(id)}`, json("PATCH", body)),
    onMutate: ({ id, ...body }) => {
      patchCache((old) => ({ ...old, smartFolders: old.smartFolders.map((s) => (s.id === id ? { ...s, ...body } : s)) }));
    },
    onSettled: () => void invalidate(),
  });
  const deleteSmartFolder = useMutation({
    mutationFn: (id: string) => webApi<{ ok: true }>(`/library/smart-folders/${encodeURIComponent(id)}`, json("DELETE")),
    onSuccess: (_r, id) => {
      patchCache((old) => ({ ...old, smartFolders: old.smartFolders.filter((s) => s.id !== id) }));
      void invalidate();
    },
  });
  const addMidi = useMutation({
    mutationFn: (body: NewMidiRequest) => webApi<MyMidi>("/library/midi", json("POST", body)),
    onSuccess: (midi) => {
      patchCache((old) => ({ ...old, midi: [midi, ...old.midi] }));
      void invalidate();
    },
  });

  /** A row auditioned or placed: the account's recent ring moves it to the
   * front. Optimistic and fire-and-forget — a lost touch is nothing. */
  const touch = useMutation({
    mutationFn: (body: TouchRecentRequest) => webApi<{ ok: true }>("/library/recent", json("POST", body)),
    onMutate: ({ key }) => {
      patchCache((old) => ({ ...old, recent: [key, ...(old.recent ?? []).filter((k) => k !== key)].slice(0, 50) }));
    },
  });
  /** An upload, undone: its clips go, while the done line still shows. */
  const undoBatch = useMutation({
    mutationFn: (batchId: string) => webApi<{ ok: true; removed: number; usedBytes: number }>(`/library/clips/batch/${encodeURIComponent(batchId)}`, json("DELETE")),
    onSuccess: (res, batchId) => {
      patchCache((old) => ({ ...old, clips: old.clips.filter((c) => c.batchId !== batchId), usedBytes: res.usedBytes }));
      void invalidate();
    },
  });

  /** Put freshly added clips in place without waiting for a refetch. */
  const added = useCallback(
    (clips: MyClip[], usedBytes: number) => {
      patchCache((old) => ({ ...old, clips: [...clips, ...old.clips], usedBytes }));
      void invalidate();
    },
    [patchCache, invalidate],
  );

  const data = query.data ?? EMPTY;
  const libraryKeys = useMemo(() => new Set(data.clips.map((c) => c.audioKey)), [data.clips]);

  return {
    signedIn: userId !== null,
    clips: data.clips,
    midi: data.midi,
    folders: data.folders,
    smartFolders: data.smartFolders,
    usedBytes: data.usedBytes,
    quotaBytes: data.quotaBytes,
    loading: query.isPending && userId !== null,
    error: query.error,
    /** Ask again after a failed load. */
    retry: () => void query.refetch(),
    remove: (id: string) => remove.mutateAsync(id),
    removeMidi: (id: string) => removeMidi.mutateAsync(id),
    removing: remove.isPending ? remove.variables : removeMidi.isPending ? removeMidi.variables : null,
    createFolder: (body: CreateFolderRequest) => createFolder.mutateAsync(body),
    updateFolder: (id: string, body: UpdateFolderRequest) => updateFolder.mutateAsync({ id, ...body }),
    deleteFolder: (id: string) => deleteFolder.mutateAsync(id),
    move: (body: MoveLibraryRequest) => move.mutateAsync(body),
    updateClip: (id: string, body: UpdateClipRequest) => updateClip.mutateAsync({ id, ...body }),
    updateMidi: (id: string, body: UpdateMidiRequest) => updateMidi.mutateAsync({ id, ...body }),
    tagMany: (body: BulkTagRequest) => tagMany.mutateAsync(body),
    review: (body: ReviewTagsRequest) => review.mutateAsync(body),
    renameTag: (body: RenameTagRequest) => renameTag.mutateAsync(body),
    addMidi: (body: NewMidiRequest) => addMidi.mutateAsync(body),
    createSmartFolder: (body: CreateSmartFolderRequest) => createSmartFolder.mutateAsync(body),
    updateSmartFolder: (id: string, body: UpdateSmartFolderRequest) => updateSmartFolder.mutateAsync({ id, ...body }),
    deleteSmartFolder: (id: string) => deleteSmartFolder.mutateAsync(id),
    added,
    /** The account's recent ring, newest first. */
    recent: data.recent ?? [],
    touch: (key: string) => void touch.mutate({ key }),
    undoBatch: (batchId: string) => undoBatch.mutateAsync(batchId),
    /** The saved seeds that use a clip, fetched when asked. */
    usesOf: (id: string) => webApi<ClipUsesResponse>(`/library/clips/${encodeURIComponent(id)}/uses`),
    /** Where the owner fetches a Mine clip's audio, by its pool key — for local rooms. */
    urlOfKey: (key: string): string | undefined => (libraryKeys.has(key) ? objectUrl(key) : undefined),
  };
}

/** "1.2 GB" / "340 MB". */
export function formatBytes(b: number): string {
  if (b <= 0) return "0 MB";
  return b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(1)} GB` : `${Math.max(1, Math.round(b / 1024 ** 2))} MB`;
}

/** "1.2 GB of 2 GB" / "340 MB of 2 GB". */
export function usageText(used: number, quota: number): string {
  return `${formatBytes(used)} of ${formatBytes(quota)}`;
}
